/**
 * AppLauncher — Unified add/search button
 *
 * Renders as a compact "+" button. Click or ⌘K opens the dropdown
 * with search and app list. Replaces both the old AppLauncher
 * trigger and the QuickAdd button.
 */



import { useState, useRef, useEffect, useMemo } from 'react'
import { useShellStore } from '@/store/shell-store'
import { useHotkeys } from '@/hooks/useHotkeys'
import {
  colors,
  typography,
  spacing,
  borderRadius,
  transitions,
  zIndex,
} from '@/lib/tokens'

interface AppLauncherProps {
  isOpen?: boolean
  onToggle?: () => void
  onClose?: () => void
}

export function AppLauncher({ isOpen: controlledOpen, onToggle, onClose }: AppLauncherProps) {
  const { availableApps, openApp } = useShellStore()
  const [internalOpen, setInternalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const isOpen = controlledOpen ?? internalOpen
  const setIsOpen = (open: boolean) => {
    if (open) {
      onToggle?.()
    } else {
      onClose?.()
    }
    setInternalOpen(open)
  }

  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const modKey = isMac ? '⌘' : 'Ctrl'

  // Hotkeys
  useHotkeys('mod+k', () => {
    if (isOpen) {
      onClose?.()
      setInternalOpen(false)
    } else {
      onToggle?.()
      setInternalOpen(true)
    }
  })
  useHotkeys('mod+p', () => {
    if (!isOpen) {
      onToggle?.()
      setInternalOpen(true)
    }
  })
  useHotkeys('escape', () => {
    onClose?.()
    setInternalOpen(false)
  }, { enabled: isOpen })

  // Click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose?.()
        setInternalOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen])

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSelectedIndex(0)
    }
  }, [isOpen])

  const filteredApps = useMemo(() => {
    if (!searchQuery.trim()) return availableApps
    const query = searchQuery.toLowerCase()
    return availableApps.filter(app => {
      const searchText = `${app.code} ${app.name} ${app.description}`.toLowerCase()
      return searchText.includes(query)
    })
  }, [availableApps, searchQuery])

  useEffect(() => {
    setSelectedIndex(0)
  }, [filteredApps])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => Math.min(prev + 1, filteredApps.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filteredApps[selectedIndex]) {
        handleSelectApp(filteredApps[selectedIndex].appId)
      }
    }
  }

  const handleSelectApp = (appId: string) => {
    openApp(appId)
    onClose?.()
    setInternalOpen(false)
  }

  const handleToggle = () => {
    if (isOpen) {
      onClose?.()
      setInternalOpen(false)
    } else {
      onToggle?.()
      setInternalOpen(true)
    }
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Compact + button */}
      <button
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          background: isOpen ? colors.cardHover : 'transparent',
          border: `1px solid ${isOpen ? colors.primary : colors.border}`,
          borderRadius: borderRadius.sm,
          color: isOpen ? colors.primary : colors.textSecondary,
          fontSize: typography.fontSize.base,
          cursor: 'pointer',
          transition: transitions.fast,
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = colors.primary
            e.currentTarget.style.color = colors.primary
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = colors.border
            e.currentTarget.style.color = colors.textSecondary
          }
        }}
        title={`Abrir App (${modKey}+K)`}
      >
        +
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          right: 0,
          background: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: borderRadius.md,
          padding: spacing.sm,
          minWidth: '320px',
          maxWidth: '420px',
          zIndex: zIndex.dropdown,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
        }}>
          {/* Search input */}
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar apps..."
            style={{
              width: '100%',
              padding: `6px ${spacing.sm}`,
              background: colors.background,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.sm,
              color: colors.textPrimary,
              fontSize: typography.fontSize.sm,
              fontFamily: typography.fontFamily.sans,
              outline: 'none',
              marginBottom: spacing.sm,
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = colors.primary
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = colors.border
            }}
          />

          {/* Results */}
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredApps.length === 0 ? (
              <div style={{
                padding: spacing.md,
                textAlign: 'center',
                color: colors.textMuted,
                fontSize: typography.fontSize.sm,
                fontFamily: typography.fontFamily.sans,
              }}>
                No se encontraron apps
              </div>
            ) : (
              filteredApps.map((app, index) => (
                <button
                  key={app.appId}
                  onClick={() => handleSelectApp(app.appId)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.sm,
                    width: '100%',
                    padding: `6px ${spacing.sm}`,
                    background: index === selectedIndex ? colors.cardHover : 'transparent',
                    border: 'none',
                    borderRadius: borderRadius.sm,
                    color: colors.textPrimary,
                    cursor: 'pointer',
                    transition: transitions.fast,
                    textAlign: 'left',
                  }}
                >
                  <span style={{
                    fontSize: typography.fontSize.lg,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                  }}>
                    {app.icon.startsWith('/') ? (
                      <img src={app.icon} alt="" style={{ width: 20, height: 20 }} />
                    ) : (
                      app.icon
                    )}
                  </span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing.xs,
                    }}>
                      <span style={{
                        fontSize: typography.fontSize.sm,
                        fontWeight: typography.fontWeight.medium,
                        fontFamily: typography.fontFamily.sans,
                      }}>
                        {app.name}
                      </span>
                      <span style={{
                        fontSize: '9px',
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                      }}>
                        {app.code}
                      </span>
                    </div>
                  </div>

                  {index === selectedIndex && (
                    <span style={{
                      fontSize: typography.fontSize.xs,
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      flexShrink: 0,
                    }}>
                      ⏎
                    </span>
                  )}
                </button>
              ))
            )}
          </div>

          {/* Footer hints */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            marginTop: spacing.sm,
            paddingTop: spacing.sm,
            borderTop: `1px solid ${colors.border}`,
            fontSize: '9px',
            color: colors.textMuted,
            fontFamily: typography.fontFamily.mono,
          }}>
            <span>↑↓ navegar</span>
            <span>⏎ abrir</span>
            <span>esc cerrar</span>
          </div>
        </div>
      )}
    </div>
  )
}
